import { useEffect, useState } from "react";
import TreeView from "../TreeView/TreeView";
import "./LevelSelector.css";
import ModalComponent from "../Modal/ModalComponent";
import { useAuth } from "../../contexts/auth";
import { useUserStore } from "../../contexts/userStore";
import Button from "../Button/Button";
import ToolTip from "../Tooltip/Tooltip";
import { MdOutlineDelete } from "react-icons/md";
import { TbListTree } from "react-icons/tb";
import { PiDotOutlineThin } from "react-icons/pi";
import { NotificationManager } from "react-notifications";

export const LevelSelector = ({ onChange: onChangeCallback, title, description, singleLevel = false, showRoot = true, levels = [], ...props }) => {
	const [treeData, setTreeData] = useState([]);
	const [modalLoadingIsOpen, setModalLoadingIsOpen] = useState(false);
	const [selectedLevels, setSelectedLevels] = useState(levels || []);
	const [selectedIds, setSelectedIds] = useState([]);

	const { condominiums, hasHierarchyAccess } = useUserStore();
	const { user } = useAuth();

	useEffect(() => {
		// verifica se houve mudança nos levels externos
		if (JSON.stringify(levels) !== JSON.stringify(selectedLevels)) {
			setSelectedLevels(levels.map(level => ({
				unitId: isNaN(level.unitId) ? 'root' : level.unitId,
				childrenAuthorized: level.childrenAuthorized
			})) || []);
		}
	}, [levels]);

	function onChange(levels) {
		onChangeCallback?.(levels.map(level => ({
			unitId: isNaN(level.unitId) ? 'root' : level.unitId,
			childrenAuthorized: level.childrenAuthorized
		})));
	}

	useEffect(() => {
		setTreeData(prevTreeData => {
			const units = (user.userData.isSuperAdmin || user.userData.person.hasRootAccess) ? [...condominiums] : [...condominiums.filter(unit => user.userData.selectedOrganizationId === unit.organizationId)];
			const children = buildTreeUnits(units.sort((unitA, unitB) => unitA.name.localeCompare(unitB.name)));
			if (showRoot && (user.userData.isSuperAdmin || user.userData.hasRootAccess)) {
				return [
					{
						id: 'root',
						name: 'Bravas Root',
						children,
					}
				];
			}
			return children;
		})
		if (props.type === 'cadastro' && user.userData.selectedUnit) {
			const selectedUnit = user.userData.selectedUnit;
			const hierarchyAccess = hasHierarchyAccess(selectedUnit.id);
			setSelectedLevels(prev => {
				const newLevels = [{
					unitId: user.userData.selectedUnit.id,
					childrenAuthorized: hierarchyAccess,
				}]
				onChange?.(newLevels);
				return newLevels;
			});
		}
	}, [condominiums, props.type, showRoot]);

	function buildTreeUnits(units) {
		function traverseUnits(unit) {
			if (!unit) return;
			units.forEach((u, idx) => {
				if (unit.id !== u.parentId) {
					return;
				}
				unit.children = unit.children || [];
				unit.children.push(traverseUnits({ ...u }));
			});
			return unit;
		}
		const roots = units.filter(unit => unit.parentId === null);
		const orphans = units.filter(unit => unit.parentId !== null && !units.find(u => u.id === unit.parentId));
		roots.push(...orphans);
		const tree = [];
		if (roots.length > 0) {
			roots?.forEach((root) => {
				tree.push(traverseUnits({ ...root, name: `${root.name}` }));
			});
		}

		return tree;
	}

	function addLevel() {
		if (singleLevel) {
			setSelectedLevels(prevLevels => {
				const newLevels = [{
					unitId: selectedIds[0],
				}];
				onChange?.(newLevels);
				return newLevels;
			});
			setModalLoadingIsOpen(false);
			return;
		}
		setSelectedLevels(prevLevels => {
			const newLevels = [...prevLevels];
			selectedIds.forEach((id) => {
				const targetUnit = condominiums.find(c => c.id === id);
				const hasParentHierarchy = newLevels.some(level => {
					const parentUnit = condominiums.find(c => c.id === level.unitId);
					if (!parentUnit || !level.childrenAuthorized) return false;
					return targetUnit?.fullHierarchyPath.startsWith(parentUnit?.fullHierarchyPath)
				});
				if (hasParentHierarchy) {
					NotificationManager.warning("", `A unidade ${condominiums.find(c => c.id === id)?.name} é filha de uma unidade que está com toda sua hierarquia marcada, primeiro desmarque a unidade pai que está com toda hierarquia marcada.`, 5000);
					return newLevels;
				}
				if (!newLevels.find(level => level.unitId === id)) {
					newLevels.push({
						unitId: id,
					});
				}
			});
			onChange?.(newLevels);
			return newLevels;
		});
		setModalLoadingIsOpen(false);
	}

	function deleteLevel(unitId) {
		setSelectedLevels(prevLevels => {
			const newLevels = prevLevels.filter(level => level.unitId !== unitId);
			onChange?.(newLevels);
			return newLevels;
		});
	}

	return (
		<>
			<div className="levels-selector">
				<div className="input-tooltip-container">
					{title ?? 'Níveis alvos'}
					<ToolTip
						tooltipVisible={true}
						tooltipText={description ?? "Os níveis alvos determinam o escopo em que a política irá atuar para conceder permissões ao usuário. Selecione os níveis apropriados para garantir que o usuário tenha as permissões corretas."}
					/>
				</div>
				<div className="selected-levels-list mb-0!">
					{selectedLevels.length > 0 ? (
						<>
							{selectedLevels.map((level) => (
								<div key={level.unitId} className="selected-level-item">
									<div className="selected-level-item-info">
										<h3>
											<span>
												{level.childrenAuthorized ? (
													<TbListTree size={18} />
												) : (
													<PiDotOutlineThin size={32} />
												)}
											</span>
											{condominiums.find(c => c.id === level.unitId)?.name || 'Bravas Root'}
										</h3>
										<label disabled={props.type === 'consulta' || !hasHierarchyAccess(level.unitId)}>
											Toda hierarquia
											<input
												type="checkbox"
												disabled={props.type === 'consulta' || !hasHierarchyAccess(level.unitId)}
												checked={level.childrenAuthorized || false}
												onChange={e => {
													setSelectedLevels(prevLevels => {
														const newLevels = prevLevels.map(lvl => {
															if (lvl.unitId === level.unitId) {
																return { ...lvl, childrenAuthorized: hasHierarchyAccess(level.unitId) && e.target.checked };
															}
															return lvl;
														});
														onChange?.(newLevels);
														return newLevels;
													});
												}}
											/>
										</label>
									</div>
									<button type="button" className="btn-delete-item" onClick={e => { deleteLevel(level.unitId) }}><MdOutlineDelete size={18} /></button>
								</div>
							))}
						</>
					) : (
						<div className="no-levels-selected">Nenhum nível selecionado</div>
					)}
				</div>
				{(!singleLevel || (singleLevel && selectedLevels.length === 0)) && (
					<div className="container-buttons mt-1.5!">
						<Button
							label={singleLevel ? "Selecionar nível" : "Adicionar nível"}
							action="add"
							onClick={addLevels => setModalLoadingIsOpen(true)}
							className="button-link"
						/>
						{selectedLevels?.length > 2 && (
							<Button
								className={"button-red"}
								label={"Remover todos"}
								action="delete"
								onClick={() => setSelectedLevels([])}
							></Button>
						)}
					</div>
				)}
			</div>
			{modalLoadingIsOpen && (
				<ModalComponent
					modalIsOpen={modalLoadingIsOpen}
					modalText={"Selecione o nível"}
					handleConfirm={addLevel}
					handleCancel={() => { setModalLoadingIsOpen(false); }}
					hideButtons={false}
				>
					<div className="modal-levels-selector-content">
						<TreeView data={treeData} onChange={setSelectedIds} singleCheck={true} />
					</div>
				</ModalComponent>
			)}
		</>
	)
}